import React, { useContext, useState } from 'react';
import { Study, Reference, Keyword } from '../StudyList';
import { LibkeyContext } from '../components/LibkeyProvider';
import * as styles from './StudyReference.module.scss';
import { ReferenceActions } from './ReferenceActions';
import { LibkeyAlertBanner } from './LibkeyAlertBanner';
import { useStudyList } from './StudyListProvider';
import { updateReviewReference } from 'query/study/ReviewReference';
import { Heading, KeywordHighlighter, Link, Text } from 'components/core';
import { Inline, Stack } from 'components/layout';
import { splitOnDOI } from 'util/doi';
import { hasValue } from 'util/hasValue';
import { FileUploadDialog } from 'components/FileUploadDialog';

type ReferenceSectionProps = {
  reviewId: string;
  showAbstracts: boolean;
  reference: Reference;
  includedKeywords: Keyword | null;
  excludedKeywords: Keyword | null;
  /**
   * Whether the parent study has more than one reference. Multi-reference
   * studies show the LibKey alert banner above each flagged reference here;
   * single-reference studies show it at the top of the card instead.
   */
  isMultiReference: boolean;
  onReviewReferenceUpdated: (url: string) => Promise<any>;
};

function coalesce(value: string | null, fallback: string): string {
  if (hasValue(value)) {
    return value;
  } else {
    return fallback;
  }
}

type DoiLinkProps = {
  rawDoi: string | null;
  refId: string | null;
};

const DoiLink = ({ rawDoi, refId }: DoiLinkProps) => {
  // `isEnabled` is true when native LibKey links render (own library or the
  // Open Alex default); `nomadMigrationEligible` is true while the reviewer
  // has not selected their own library; `isResolved` is false while the
  // reviewer is still loading. The bare-render default (no provider) reports
  // resolved-and-disabled.
  const {
    isEnabled: nativeLibkeyActive,
    nomadMigrationEligible,
    isResolved,
  } = useContext(LibkeyContext);

  if (!(rawDoi || refId)) {
    return null;
  }

  const processedDoi = rawDoi && splitOnDOI(rawDoi)[1];
  let urlToDoi = null;

  let linkText = '';

  if (processedDoi) {
    urlToDoi = `https://dx.doi.org/${processedDoi}`;
    linkText = processedDoi;
  } else if (rawDoi) {
    linkText = rawDoi;
    urlToDoi = rawDoi;
  }

  // `ref-ids` is purely the hook the LibKey Nomad chrome extension uses. We
  // emit it only once the reviewer state has resolved, and suppress it only
  // when the reviewer has selected their own library (native active AND no
  // longer migration-eligible). A reviewer on the Open Alex default must keep
  // the hook: the Nomad migration nudge harvests their library id from the
  // buttons Nomad injects against it.
  const extensionHookClass =
    isResolved && (nomadMigrationEligible || !nativeLibkeyActive)
      ? 'ref-ids'
      : undefined;

  return (
    <Text variant="weak">
      <Inline
        spacing={1}
        alignX="left"
        component="span"
        className={extensionHookClass}
      >
        {urlToDoi && (
          <>
            <span>DOI:</span>
            <Link external={true} variant="primary" to={urlToDoi}>
              {linkText}
            </Link>
          </>
        )}
        {processedDoi && refId && <Text component="span">•</Text>}
        {refId && <Text component="span">Ref ID: {refId}</Text>}
      </Inline>
    </Text>
  );
};

const ReferenceSection = ({
  reviewId,
  reference,
  showAbstracts,
  includedKeywords,
  excludedKeywords,
  isMultiReference,
  onReviewReferenceUpdated,
}: ReferenceSectionProps) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUploadedFiles, setShowUploadedFiles] = useState(false);
  const {
    isHighlightsEnabled,
    hasFullTextFeature,
    saveFullTextDocument,
  } = useStudyList();
  const title = coalesce(reference.title, '[Untitled reference]');
  const authors = coalesce(reference.authors, '[Unknown authors]');
  const rawDoi = reference.doi;
  const refId = reference.ref_id;

  return (
    <div key={reference.id} className={styles.reference}>
      <Stack spacing={2} alignX="left" className={styles.content}>
        {isMultiReference && (
          <LibkeyAlertBanner
            referenceId={reference.id}
            className={styles.alertBanner}
          />
        )}
        <Heading type="h5" component="h3" className={styles.title}>
          <KeywordHighlighter
            text={title}
            inclusionWords={includedKeywords?.title || []}
            exclusionWords={excludedKeywords?.title || []}
            isHighlightingEnabled={isHighlightsEnabled}
          />
        </Heading>
        <div className={styles.metadata}>
          <Text variant="weak">{authors}</Text>
          {reference.source ? (
            <Text variant="weak">{reference.source}</Text>
          ) : (
            <>
              <Text variant="weak">{reference.journal_info}</Text>
              <Text variant="weak">{reference.publisher_info}</Text>
            </>
          )}
          <DoiLink rawDoi={rawDoi} refId={refId} />
        </div>

        <ReferenceActions
          reference={reference}
          documentUrl={reference.document_url}
          showAbstracts={showAbstracts}
          inclusionWords={includedKeywords?.abstract || []}
          exclusionWords={excludedKeywords?.abstract || []}
          showFullTextFeatures={hasFullTextFeature}
          setShowUploadModal={() => setShowUploadModal(true)}
          showUploadedFiles={showUploadedFiles}
        />

        <FileUploadDialog
          reviewId={reviewId}
          isModalOpen={showUploadModal}
          saveDocument={saveFullTextDocument}
          submitDocumentUrl={async (url) => {
            return onReviewReferenceUpdated(url);
          }}
          reference={reference}
          onClose={() => {
            setShowUploadModal(false);
            setShowUploadedFiles(true);
          }}
        />
      </Stack>
    </div>
  );
};

type StudyReferenceProps = {
  reviewId: string;
  study: Study;
  showAbstracts: boolean;
};

export const StudyReference = ({
  reviewId,
  study,
  showAbstracts,
}: StudyReferenceProps) => {
  const { onFullTextLinkUpdated } = useStudyList();

  return (
    <div className={styles.studyReference}>
      <Inline spacing={2} alignX="left">
        <Text>
          #{study.unique_index}
          {study.study_id && ` - ${study.study_id}`}
        </Text>
        {study.references.length > 1 && (
          <Text variant="weak">
            [{study.references.length} associated references]
          </Text>
        )}
      </Inline>
      <div>
        {study.references.map((reference) => (
          <ReferenceSection
            key={reference.id}
            reference={reference}
            showAbstracts={showAbstracts}
            includedKeywords={study.included_keywords}
            excludedKeywords={study.excluded_keywords}
            reviewId={reviewId}
            isMultiReference={study.references.length > 1}
            onReviewReferenceUpdated={(url) => {
              return updateReviewReference({
                reviewId,
                studyId: study.id,
                reviewReferenceId: reference.id,
                params: { document_url: url },
              }).then(() => {
                onFullTextLinkUpdated(reference.id, url);
              });
            }}
          />
        ))}
      </div>
    </div>
  );
};
